/**
 * GENERATED FILE — do not edit by hand.
 * Regenerate with: npm run gen:fluid-catalogue
 * (script: scripts/build-fluid-catalogue.ts)
 *
 * Source: coolprop-wasm 7.2.1dev — CoolProp::get_global_param_string('fluids_list')
 * plus per-fluid get_fluid_param_string(name, 'CAS' | 'aliases' | 'pure').
 * Transport flags were probed in isolated child processes (a WASM abort on
 * one fluid can poison the heap for the next — see the script header).
 *
 * Scope: HEOS pure and pseudo-pure fluids ONLY.  No INCOMP fluids, no
 * REFPROP-only names, no arbitrary mixture strings.
 */
export type FluidTransportFlag = "yes" | "no" | "unknown";

export interface FluidCatalogueEntry {
  /** Canonical HEOS name (exactly as it appears in fluids_list). */
  readonly name: string;
  /** CAS registry number, or a pseudo-pure identifier such as "R404A.PPF". */
  readonly cas: string;
  /** true = pure fluid; false = pseudo-pure mixture (Air, R404A, …). */
  readonly pure: boolean;
  /** CoolProp-registered aliases (the canonical name is not repeated). */
  readonly aliases: readonly string[];
  /**
   * Transport-model availability, probed at generation time:
   *  - 'yes'     a model exists and returned a finite positive value;
   *  - 'no'      a state flashed successfully but the model is absent;
   *  - 'unknown' no probe state flashed — treat as absent for validation,
   *             but do not hard-depend on it.
   */
  readonly transport: {
    readonly viscosity: FluidTransportFlag;
    readonly conductivity: FluidTransportFlag;
  };
}

/** Every HEOS fluid of the shipped coolprop-wasm build, sorted by name. */
export const FLUID_CATALOGUE = [
  {
    name: "1-Butene",
    cas: "106-98-9",
    pure: true,
    aliases: ["1Butene", "1BUTENE", "1-BUTENE", "Butene"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "Acetone",
    cas: "67-64-1",
    pure: true,
    aliases: ["acetone", "ACETONE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "Air",
    cas: "AIR.PPF",
    pure: false,
    aliases: ["air", "AIR", "R729"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "Ammonia",
    cas: "7664-41-7",
    pure: true,
    aliases: ["NH3", "ammonia", "R717", "AMMONIA"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "Argon",
    cas: "7440-37-1",
    pure: true,
    aliases: ["argon", "ARGON", "R740", "Ar"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "Benzene",
    cas: "71-43-2",
    pure: true,
    aliases: ["benzene", "BENZENE"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "CarbonDioxide",
    cas: "124-38-9",
    pure: true,
    aliases: ["R744", "co2", "CO2", "carbondioxide", "CARBONDIOXIDE"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "CarbonMonoxide",
    cas: "630-08-0",
    pure: true,
    aliases: ["CO", "CARBONMONOXIDE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "CarbonylSulfide",
    cas: "463-58-1",
    pure: true,
    aliases: ["COS", "CARBONYLSULFIDE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "CycloHexane",
    cas: "110-82-7",
    pure: true,
    aliases: ["Cyclohexane", "CYCLOHEXANE", "CYCLOHEX"],
    transport: { viscosity: "yes", conductivity: "no" },
  },
  {
    name: "CycloPropane",
    cas: "75-19-4",
    pure: true,
    aliases: ["cyclopropane", "Cyclopropane", "CYCLOPROPANE", "CYCLOPRO"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "Cyclopentane",
    cas: "287-92-3",
    pure: true,
    aliases: ["CycloPentane", "cyclopentane", "CYCLOPENTANE", "CYCLOPEN"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "D4",
    cas: "556-67-2",
    pure: true,
    aliases: ["Octamethylcyclotetrasiloxane", "OCTAMETHYLCYCLOTETRASILOXANE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "D5",
    cas: "541-02-6",
    pure: true,
    aliases: ["Decamethylcyclopentasiloxane", "DECAMETHYLCYCLOPENTASILOXANE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "D6",
    cas: "540-97-6",
    pure: true,
    aliases: ["Dodecamethylcyclohexasiloxane", "DODECAMETHYLCYCLOHEXASILOXANE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "Deuterium",
    cas: "7782-39-0",
    pure: true,
    aliases: ["deuterium", "DEUTERIUM", "D2"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "Dichloroethane",
    cas: "107-06-2",
    pure: true,
    aliases: [
      "DICHLOROETHANE",
      "1",
      "2-dichloroethane",
      "1",
      "2-DICHLOROETHANE",
    ],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "DiethylEther",
    cas: "60-29-7",
    pure: true,
    aliases: ["DEE", "DiethylEther"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "DimethylCarbonate",
    cas: "616-38-6",
    pure: true,
    aliases: ["DMC", "dimethylcarbonate", "DIMETHYLCARBONATE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "DimethylEther",
    cas: "115-10-6",
    pure: true,
    aliases: ["DIMETHYLETHER", "DME"],
    transport: { viscosity: "yes", conductivity: "no" },
  },
  {
    name: "Ethane",
    cas: "74-84-0",
    pure: true,
    aliases: ["ethane", "ETHANE", "R170", "n-C2H6"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "Ethanol",
    cas: "64-17-5",
    pure: true,
    aliases: ["C2H6O", "ethanol", "ETHANOL"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "EthylBenzene",
    cas: "100-41-4",
    pure: true,
    aliases: ["ethylbenzene", "ETHYLBENZENE", "EBENZENE"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "Ethylene",
    cas: "74-85-1",
    pure: true,
    aliases: ["ethylene", "ETHYLENE", "R1150"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "EthyleneOxide",
    cas: "75-21-8",
    pure: true,
    aliases: ["ETHYLENEOXIDE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "Fluorine",
    cas: "7782-41-4",
    pure: true,
    aliases: ["fluorine", "FLUORINE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "HFE143m",
    cas: "421-14-7",
    pure: true,
    aliases: ["HFE-143m", "HFE143M", "HFE-143M", "RE143A", "RE143a"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "HeavyWater",
    cas: "7789-20-0",
    pure: true,
    aliases: ["D2O", "HEAVYWATER"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "Helium",
    cas: "7440-59-7",
    pure: true,
    aliases: ["helium", "HELIUM", "He", "R704"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "Hydrogen",
    cas: "1333-74-0",
    pure: true,
    aliases: ["hydrogen", "HYDROGEN", "H2", "R702"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "HydrogenChloride",
    cas: "7647-01-0",
    pure: true,
    aliases: ["HydrogenChloride", "HYDROGENCHLORIDE", "HCl", "HCL"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "HydrogenSulfide",
    cas: "7783-06-4",
    pure: true,
    aliases: ["H2S", "HYDROGENSULFIDE"],
    transport: { viscosity: "yes", conductivity: "no" },
  },
  {
    name: "IsoButane",
    cas: "75-28-5",
    pure: true,
    aliases: [
      "isobutane",
      "Isobutane",
      "ISOBUTANE",
      "R600A",
      "R600a",
      "ISOBUTAN",
    ],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "IsoButene",
    cas: "115-11-7",
    pure: true,
    aliases: ["Isobutene", "ISOBUTENE", "IBUTENE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "Isohexane",
    cas: "107-83-5",
    pure: true,
    aliases: ["ihexane", "ISOHEXANE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "Isopentane",
    cas: "78-78-4",
    pure: true,
    aliases: ["ipentane", "R601a", "ISOPENTANE", "IPENTANE"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "Krypton",
    cas: "7439-90-9",
    pure: true,
    aliases: ["krypton", "KRYPTON"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "MD2M",
    cas: "141-62-8",
    pure: true,
    aliases: ["Decamethyltetrasiloxane", "DECAMETHYLTETRASILOXANE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "MD3M",
    cas: "141-63-9",
    pure: true,
    aliases: ["Dodecamethylpentasiloxane", "DODECAMETHYLPENTASILOXANE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "MD4M",
    cas: "107-52-8",
    pure: true,
    aliases: ["Tetradecamethylhexasiloxane", "TETRADECAMETHYLHEXASILOXANE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "MDM",
    cas: "107-51-7",
    pure: true,
    aliases: ["Octamethyltrisiloxane", "OCTAMETHYLTRISILOXANE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "MM",
    cas: "107-46-0",
    pure: true,
    aliases: ["Hexamethyldisiloxane", "HEXAMETHYLDISILOXANE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "Methane",
    cas: "74-82-8",
    pure: true,
    aliases: ["CH4", "methane", "METHANE", "R50", "n-C1H4"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "Methanol",
    cas: "67-56-1",
    pure: true,
    aliases: ["methanol", "METHANOL"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "MethylLinoleate",
    cas: "112-63-0",
    pure: true,
    aliases: ["METHYLLINOLEATE", "MLINOLEA"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "MethylLinolenate",
    cas: "301-00-8",
    pure: true,
    aliases: ["METHYLLINOLENATE", "MLINOLEN"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "MethylOleate",
    cas: "112-62-9",
    pure: true,
    aliases: ["METHYLOLEATE", "MOLEATE"],
    transport: { viscosity: "unknown", conductivity: "unknown" },
  },
  {
    name: "MethylPalmitate",
    cas: "112-39-0",
    pure: true,
    aliases: ["METHYLPALMITATE", "MPALMITA"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "MethylStearate",
    cas: "112-61-8",
    pure: true,
    aliases: ["METHYLSTEARATE", "MSTEARAT"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "Neon",
    cas: "7440-01-9",
    pure: true,
    aliases: ["neon", "NEON", "R720"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "Neopentane",
    cas: "463-82-1",
    pure: true,
    aliases: ["neopentn", "NEOPENTANE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "Nitrogen",
    cas: "7727-37-9",
    pure: true,
    aliases: ["nitrogen", "NITROGEN", "N2", "R728"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "NitrousOxide",
    cas: "10024-97-2",
    pure: true,
    aliases: ["N2O", "NITROUSOXIDE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "Novec649",
    cas: "756-13-8",
    pure: true,
    aliases: ["Novec1230", "NOVEC649"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "OrthoDeuterium",
    cas: "7782-39-0o",
    pure: true,
    aliases: ["orthodeuterium", "ORTHODEUTERIUM"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "OrthoHydrogen",
    cas: "1333-74-0o",
    pure: true,
    aliases: ["Orthohydrogen", "orthohydrogen", "ORTHOHYDROGEN", "ORTHOHYD"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "Oxygen",
    cas: "7782-44-7",
    pure: true,
    aliases: ["oxygen", "OXYGEN", "O2", "R732"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "ParaDeuterium",
    cas: "7782-39-0p",
    pure: true,
    aliases: ["paradeuterium", "PARADEUTERIUM"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "ParaHydrogen",
    cas: "1333-74-0p",
    pure: true,
    aliases: ["Parahydrogen", "parahydrogen", "PARAHYDROGEN", "PARAHYD"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "Propylene",
    cas: "115-07-1",
    pure: true,
    aliases: ["propylene", "PROPYLENE", "PROPYLEN", "R1270"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "Propyne",
    cas: "74-99-7",
    pure: true,
    aliases: ["propyne", "PROPYNE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "R11",
    cas: "75-69-4",
    pure: true,
    aliases: [],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R113",
    cas: "76-13-1",
    pure: true,
    aliases: [],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "R114",
    cas: "76-14-2",
    pure: true,
    aliases: [],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "R115",
    cas: "76-15-3",
    pure: true,
    aliases: [],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "R116",
    cas: "76-16-4",
    pure: true,
    aliases: [],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R12",
    cas: "75-71-8",
    pure: true,
    aliases: [],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R123",
    cas: "306-83-2",
    pure: true,
    aliases: [],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R1233zd(E)",
    cas: "102687-65-0",
    pure: true,
    aliases: ["R1233zdE", "R1233ZDE", "R1233ZD(E)", "R1233ZD"],
    transport: { viscosity: "yes", conductivity: "no" },
  },
  {
    name: "R1234yf",
    cas: "754-12-1",
    pure: true,
    aliases: ["R1234YF"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R1234ze(E)",
    cas: "29118-24-9",
    pure: true,
    aliases: ["R1234ZE", "R1234ZEE", "R1234zeE", "R1234ZE(E)"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R1234ze(Z)",
    cas: "29118-25-0",
    pure: true,
    aliases: ["R1234ZE(Z)", "R1234ZEZ"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "R124",
    cas: "2837-89-0",
    pure: true,
    aliases: [],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R1243zf",
    cas: "677-21-4",
    pure: true,
    aliases: ["R1243ZF"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "R125",
    cas: "354-33-6",
    pure: true,
    aliases: [],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R13",
    cas: "75-72-9",
    pure: true,
    aliases: [],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R1336mzz(E)",
    cas: "66711-86-2",
    pure: true,
    aliases: [
      "(E)-1",
      "1",
      "1",
      "4",
      "4",
      "4-HEXAFLUORO-2-BUTENE",
      "r1336mzz(e)",
      "R1336mzz(E)",
      "(E)-1",
      "1",
      "1",
      "4",
      "4",
      "4-Hexafluoro-2-butene",
      "R1336MZZ(E)",
      "(e)-1",
      "1",
      "1",
      "4",
      "4",
      "4-hexafluoro-2-butene",
      "R1336MZZE",
    ],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "R134a",
    cas: "811-97-2",
    pure: true,
    aliases: ["R134A"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R13I1",
    cas: "2314-97-8",
    pure: true,
    aliases: ["CF3I"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "R14",
    cas: "75-73-0",
    pure: true,
    aliases: [],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R141b",
    cas: "1717-00-6",
    pure: true,
    aliases: ["R141B"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R142b",
    cas: "75-68-3",
    pure: true,
    aliases: ["R142B"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R143a",
    cas: "420-46-2",
    pure: true,
    aliases: ["R143A"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R152A",
    cas: "75-37-6",
    pure: true,
    aliases: ["R152a"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R161",
    cas: "353-36-6",
    pure: true,
    aliases: ["Fluoroethane", "FLUOROETHANE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "R21",
    cas: "75-43-4",
    pure: true,
    aliases: [],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "R218",
    cas: "76-19-7",
    pure: true,
    aliases: [],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R22",
    cas: "75-45-6",
    pure: true,
    aliases: [],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R227EA",
    cas: "431-89-0",
    pure: true,
    aliases: ["R227ea"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R23",
    cas: "75-46-7",
    pure: true,
    aliases: [],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R236EA",
    cas: "431-63-0",
    pure: true,
    aliases: ["R236ea"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R236FA",
    cas: "690-39-1",
    pure: true,
    aliases: ["R236fa"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R245ca",
    cas: "679-86-7",
    pure: true,
    aliases: ["R245CA"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "R245fa",
    cas: "460-73-1",
    pure: true,
    aliases: ["R245FA"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R32",
    cas: "75-10-5",
    pure: true,
    aliases: [],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R365MFC",
    cas: "406-58-6",
    pure: true,
    aliases: ["R365mfc"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "R40",
    cas: "74-87-3",
    pure: true,
    aliases: ["MethylChloride"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "R404A",
    cas: "R404A.PPF",
    pure: false,
    aliases: ["R404a"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R407C",
    cas: "R407C.PPF",
    pure: false,
    aliases: ["R407c"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R41",
    cas: "593-53-3",
    pure: true,
    aliases: [],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "R410A",
    cas: "R410A.PPF",
    pure: false,
    aliases: ["R410a"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "R507A",
    cas: "R507A.PPF",
    pure: false,
    aliases: ["R507a"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "RC318",
    cas: "115-25-3",
    pure: true,
    aliases: [],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "SES36",
    cas: "SES36.ppf",
    pure: false,
    aliases: [],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "SulfurDioxide",
    cas: "7446-09-5",
    pure: true,
    aliases: ["SO2", "SULFURDIOXIDE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "SulfurHexafluoride",
    cas: "2551-62-4",
    pure: true,
    aliases: ["SULFURHEXAFLUORIDE", "SF6"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "Toluene",
    cas: "108-88-3",
    pure: true,
    aliases: ["toluene", "TOLUENE"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "Water",
    cas: "7732-18-5",
    pure: true,
    aliases: ["water", "WATER", "H2O", "h2o", "R718"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "Xenon",
    cas: "7440-63-3",
    pure: true,
    aliases: ["Xe", "xenon", "XENON"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "cis-2-Butene",
    cas: "590-18-1",
    pure: true,
    aliases: ["Cis-2-Butene", "CIS-2-BUTENE", "C2BUTENE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "m-Xylene",
    cas: "108-38-3",
    pure: true,
    aliases: ["mXylene", "m-xylene", "M-XYLENE", "MC8H10"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "n-Butane",
    cas: "106-97-8",
    pure: true,
    aliases: [
      "nButane",
      "butane",
      "Butane",
      "BUTANE",
      "N-BUTANE",
      "R600",
      "NC4H10",
      "n-C4H10",
    ],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "n-Decane",
    cas: "124-18-5",
    pure: true,
    aliases: ["Decane", "decane", "DECANE", "N-DECANE", "NC10H22", "n-C10H22"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "n-Dodecane",
    cas: "112-40-3",
    pure: true,
    aliases: [
      "nDodecane",
      "Dodecane",
      "DODECANE",
      "N-DODECANE",
      "C12",
      "NC12H26",
      "n-C12H26",
    ],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "n-Heptane",
    cas: "142-82-5",
    pure: true,
    aliases: [
      "nHeptane",
      "Heptane",
      "HEPTANE",
      "N-HEPTANE",
      "NC7H16",
      "n-C7H16",
    ],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "n-Hexane",
    cas: "110-54-3",
    pure: true,
    aliases: ["nHexane", "Hexane", "HEXANE", "N-HEXANE", "NC6H14", "n-C6H14"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "n-Nonane",
    cas: "111-84-2",
    pure: true,
    aliases: ["nonane", "Nonane", "NONANE", "N-NONANE", "NC9H20", "n-C9H20"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "n-Octane",
    cas: "111-65-9",
    pure: true,
    aliases: ["nOctane", "Octane", "OCTANE", "N-OCTANE", "NC8H18", "n-C8H18"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "n-Pentane",
    cas: "109-66-0",
    pure: true,
    aliases: [
      "nPentane",
      "Pentane",
      "PENTANE",
      "N-PENTANE",
      "R601",
      "NC5H12",
      "n-C5H12",
    ],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "n-Propane",
    cas: "74-98-6",
    pure: true,
    aliases: [
      "Propane",
      "propane",
      "R290",
      "C3H8",
      "PROPANE",
      "N-PROPANE",
      "NC3H8",
      "n-C3H8",
    ],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "n-Undecane",
    cas: "1120-21-4",
    pure: true,
    aliases: [
      "Undecane",
      "UNDECANE",
      "N-UNDECANE",
      "C11",
      "NC11H24",
      "n-C11H24",
    ],
    transport: { viscosity: "no", conductivity: "no" },
  },
  {
    name: "o-Xylene",
    cas: "95-47-6",
    pure: true,
    aliases: ["oXylene", "o-xylene", "O-XYLENE", "OC8H10"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "p-Xylene",
    cas: "106-42-3",
    pure: true,
    aliases: ["pXylene", "p-xylene", "P-XYLENE", "PC8H10"],
    transport: { viscosity: "yes", conductivity: "yes" },
  },
  {
    name: "trans-2-Butene",
    cas: "624-64-6",
    pure: true,
    aliases: ["Trans-2-Butene", "TRANS-2-BUTENE", "T2BUTENE"],
    transport: { viscosity: "no", conductivity: "no" },
  },
] as const satisfies readonly FluidCatalogueEntry[];

/** Union of every canonical HEOS fluid name in the shipped build. */
export type HeosFluidName = (typeof FLUID_CATALOGUE)[number]["name"];

export const FLUID_CATALOGUE_COUNT = FLUID_CATALOGUE.length;
